import type {
  PMWStockBackendAiAnimatedSticker,
  PMWStockBackendAiIcon,
  PMWStockBackendAiSticker,
  PMWStockBackendAnimatedSticker,
  PMWStockBackendExtractedGettySticker,
  PMWStockBackendIcon,
  PMWStockBackendShape,
  PMWStockBackendStillSticker,
} from '@Panels/manage-pmw-stock-panels/manage-pmw-stock-panels-types';
import type {GridAnimatedStickerItemStorage} from '@Components/base-grid/components/grid-animated-sticker-item';
import type {GridItemBaseStorage, GridItemStorage, GridStockMediaItemBackendData} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {isMobile} from 'react-device-detect';
import {v4 as uuidv4} from 'uuid';
import type {GridStockAudioItemBackEndData, GridStockAudioItemStorage} from '@Components/base-grid/components/grid-stock-audio-item';
import type {GridAiImageItemStorage, GridStockImageItemBackEndData, GridStockImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import {
  gettyImageUploadV2,
  gettyVideoUploadV2,
  pixabayImageUploadV2,
  pixabayVideoUploadV2,
  SOURCE,
  storyBlocksAudioUploadV2,
  storyBlocksImageUploadV2,
  storyBlocksVideoUploadV2,
} from '@Libraries/stock-graphic-library';
import {runUserAudioUploadJob, runUserImageUploadJob, runUserVideoUploadJob} from '@Libraries/user-media-library';
import {preFillAiPromptBox} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pre-fill.library';
import {AI_IMAGE_PROMPT_BOX_ID} from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box.types';
import {AI_VIDEO_PROMPT_BOX_ID} from '@Panels/ai-video-panel/ai-video-panel.types';
import type {AITextToImageBackendData, AudioCategoryBackendData, StockGetterOptions} from '@Libraries/pmw-stock-media-library';
import {getPMWStockImageThumbURL, StockSources} from '@Libraries/pmw-stock-media-library';
import {StickerItemSource} from '@Libraries/pmw-stock-media-library.types';
import type {GridStockVideoItemBackEndData, GridStockVideoItemStorage} from '@Components/base-grid/components/grid-stock-video-item';
import type {GridShapeItemStorage} from '@Components/base-grid/components/grid-shape-item';
import type {GridClipartItemBackEndData, GridClipartItemStorage} from '@Components/base-grid/components/grid-clipart-item';
import type {AiCarouselItemMeta, GridItemBaseStorageBackendData, SelectedFilter, StockFilter, StockFilterID, StockSubFilterRef} from '@Panels/stock-panel/stock-panel.types';
import type {AiPanelKey} from '@Panels/stock-panel/components/stock-ai-button/stock-ai-button.types';
import {AiMenuOpenSource, GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {
  AiCarouselContentType,
  AUDIO_CATEGORY_ID_TO_STOCK_BUTTON_FILTER,
  DISABLE_PIXABY_SOURCES,
  ICON_FILTERS_TO_CATEGORY_MAPPING,
  SHAPE_FILTERS_TO_CATEGORY_MAPPING,
  MUTUALLY_EXCLUSIVE_SUBFILTERS,
  IS_GETTY_ENABLED,
  AnimationSubFilter,
  StockButtonFilters,
  StockCarouselType,
  StockFilterTypes,
  StockGridIDs,
  StockPanelTabs,
  UHD_APPLIED_VIDEO_FILTER_ID,
} from '@Panels/stock-panel/stock-panel.types';
import type {AiVideoBackendData} from '@Libraries/ai-video.library';
import {getAiVideoThumbUrl, uploadGeneratedAiVideo} from '@Libraries/ai-video.library';
import {addGeneratedVoiceToDesign} from '@Libraries/text-to-speech.library';
import type {
  AiAnimatedStickerData,
  AnimatedStickerData,
  AudioData,
  ElementData,
  ImageData,
  PMWAiIconData,
  PMWAiStickerData,
  PMWExtractedGettyStickerData,
  PMWIconData,
  PMWShapeData,
  PMWStillStickerData,
  TempImageData,
  VideoData,
} from '@Libraries/add-media-library';
import {
  ElementDataType,
  getAudioDataFromUserAudioVO,
  getGeneratedAiImageDataFromGridItemStorage,
  getImageDataForPMWStockImage,
  getImageDataFromUserImageVO,
  getStockImageDataFromGridStockImageItemStorage,
  getVideoDataFromUserVideoVO,
} from '@Libraries/add-media-library';
import {getUserType} from '@Libraries/user.library';
import type {PanelBottomPill} from '@Components/panel/components/panel-bottom-pills';
import type {CarouselItemData} from '@Components/carousel/carousel.types';
import type {TabItemContentStorage} from '@Components/tabs';
import type {UserImageVOResponse} from '@Libraries/user-image-library';
import {getHashedFilenameForStockAsset} from '@Libraries/user-image-library';
import type {UserVideoVOResponse} from '@Libraries/user-video-library';
import {isPremiumUser, isUserStudent} from '@Utils/user.util';
import type {GridMediaItemTag} from '@Components/base-grid/components/grid-media-item';
import {GridMediaItemTagType} from '@Components/base-grid/components/grid-media-item';
import {getUrlForGeneratedAiImage, textToImageUpload} from '@Libraries/text-to-image-library';
import {capitalize} from '@Utils/string.util';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import type {AnimatedSprite} from '@Libraries/animated-sprite.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {getUrlForImageItemScreenTier, ScreenTier} from '@Libraries/image-item.library';
import {imageUrlToDataUrl} from '@Utils/image.util';
import {doesPublicFileExistAsync} from '@Utils/file.util';
import type {GridExtractedGettyStickerItemStorage} from '@Components/base-grid/components/grid-extracted-getty-sticker-item';
import type {
  GridAiIconItemBackEndData,
  GridAiIconItemStorage,
  GridIconItemBackEndData,
  GridIconItemStorage,
} from '@Components/base-grid/components/grid-icon-item/grid-icon-item.types';
import type {
  GridAiStickerItemBackEndData,
  GridAiStickerItemStorage,
  GridStillStickerItemBackEndData,
  GridStillStickerItemStorage,
} from '@Components/base-grid/components/grid-still-sticker-item/grid-still-sticker-item.types';
import {getPromptTextContentForAIImage} from '@Panels/ai-image-tooltip-text/ai-image-prompt-text.helper';
import type {StockDropPosition} from '@Panels/stock-panel/stock-drag-controller';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';

export const FALLBACK_GRID_ITEM_WIDTH = 170;
export const FALLBACK_GRID_ITEM_HEIGHT = 170;
export const STOCK_AUDIO_GRID_ITEM_WIDTH = 220;

const ANIMATED_STICKER_SOURCES = [StockSources.PMW_ANIMATED_STICKER, StockSources.PMW_AI_ANIMATED_STICKER];

export const getAnimatedStickerSources = (): StockSources[] => {
  return ANIMATED_STICKER_SOURCES;
};

const RECENT_ITEM_TYPE_TO_TAB: Partial<Record<GRID_ITEM_TYPE, StockPanelTabs>> = {
  [GRID_ITEM_TYPE.STOCK_IMAGE]: StockPanelTabs.PHOTOS,
  [GRID_ITEM_TYPE.IMAGE]: StockPanelTabs.PHOTOS,
  [GRID_ITEM_TYPE.USER_IMAGE]: StockPanelTabs.PHOTOS,
  [GRID_ITEM_TYPE.STOCK_VIDEO]: StockPanelTabs.VIDEOS,
  [GRID_ITEM_TYPE.VIDEO]: StockPanelTabs.VIDEOS,
  [GRID_ITEM_TYPE.USER_VIDEO]: StockPanelTabs.VIDEOS,
  [GRID_ITEM_TYPE.STOCK_AUDIO]: StockPanelTabs.AUDIOS,
  [GRID_ITEM_TYPE.AUDIO]: StockPanelTabs.AUDIOS,
  [GRID_ITEM_TYPE.USER_AUDIO]: StockPanelTabs.AUDIOS,
  [GRID_ITEM_TYPE.PMW_STOCK_SHAPE]: StockPanelTabs.SHAPES,
  [GRID_ITEM_TYPE.SHAPE]: StockPanelTabs.SHAPES,
  [GRID_ITEM_TYPE.PMW_STOCK_ICON]: StockPanelTabs.ICONS,
  [GRID_ITEM_TYPE.ICON]: StockPanelTabs.ICONS,
  [GRID_ITEM_TYPE.PMW_AI_ICON]: StockPanelTabs.ICONS,
  [GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.STILL_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.ANIMATED_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_AI_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER]: StockPanelTabs.STICKERS,
  [GRID_ITEM_TYPE.CLIPART]: StockPanelTabs.STICKERS,
};

// Only the single-source item types are listed — these are the ones that can appear in a blacklistedSources list
// (e.g. the slideshow blacklists animated stickers). Image/video/audio span several providers, so they have no single
// source here and are never blacklisted in practice.
const RECENT_ITEM_TYPE_TO_STOCK_SOURCE: Partial<Record<GRID_ITEM_TYPE, StockSources>> = {
  [GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER]: StockSources.PMW_ANIMATED_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER]: StockSources.PMW_AI_ANIMATED_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER]: StockSources.PMW_STILL_STICKER,
  [GRID_ITEM_TYPE.PMW_AI_STICKER]: StockSources.PMW_AI_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER]: StockSources.PMW_EXTRACTED_GETTY_STICKER,
  [GRID_ITEM_TYPE.PMW_STOCK_ICON]: StockSources.PMW_ICON,
  [GRID_ITEM_TYPE.PMW_AI_ICON]: StockSources.PMW_AI_ICON,
  [GRID_ITEM_TYPE.PMW_STOCK_SHAPE]: StockSources.PMW_SHAPE,
};

/**
 * A recent stock item belongs in this panel's Recents carousel only when its media type is one the panel shows
 * (tabsToShow) and its source is not blacklisted (e.g. animated stickers in the slideshow flow).
 */
export const isRecentStockItemAllowed = (item: GridItemStorage, tabsToShow: StockPanelTabs[], blacklistedSources: StockSources[]): boolean => {
  const tab = RECENT_ITEM_TYPE_TO_TAB[item.type];
  if (!tab || !tabsToShow.includes(tab)) {
    return false;
  }
  const source = RECENT_ITEM_TYPE_TO_STOCK_SOURCE[item.type];
  return !(source && blacklistedSources.includes(source));
};

export const animatedStickerDataToAnimatedStickerItem = (animatedStickerData: PMWStockBackendAnimatedSticker): GridAnimatedStickerItemStorage => {
  return {
    hashedFilename: animatedStickerData.hashedFilename,
    type: GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER,
    id: animatedStickerData.idPmwStockMedia,
    frameRate: Number(animatedStickerData.frameRate),
    duration: Number(animatedStickerData.duration),
    width: animatedStickerData.width !== 0 ? animatedStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: animatedStickerData.height !== 0 ? animatedStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
    highResAnimatedSprite: animatedStickerData.highResAnimatedSprite,
    screenAnimatedSprite: animatedStickerData.screenAnimatedSprite,
  };
};

export const aiAnimatedStickerDataToAiAnimatedStickerItem = (stickerData: PMWStockBackendAiAnimatedSticker): GridAnimatedStickerItemStorage => {
  return {
    hashedFilename: stickerData.hashedFilename,
    type: GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER,
    id: stickerData.idPmwStockMedia,
    frameRate: Number(stickerData.frameRate),
    duration: Number(stickerData.duration),
    width: stickerData.width !== 0 ? stickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: stickerData.height !== 0 ? stickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
    highResAnimatedSprite: stickerData.highResAnimatedSprite,
    screenAnimatedSprite: stickerData.screenAnimatedSprite,
  };
};

export const audioDataToAudioItem = (audioData: GridStockAudioItemBackEndData): GridStockAudioItemStorage => {
  return {
    id: audioData.id as string,
    type: GRID_ITEM_TYPE.STOCK_AUDIO,
    name: audioData.t as string,
    audioSrc: audioData.up as string,
    imgThumbSrc: audioData.u as string,
    duration: audioData.cl as string,
    source: SOURCE.STORYBLOCKS,
    data: {
      id: audioData.id as string,
      mood: audioData.m as string,
      genre: audioData.g as string,
    },
  };
};

export const clipartDataToClipartItem = (clipartData: GridClipartItemBackEndData, source: StockSources): GridClipartItemStorage => {
  let clipartSource: SOURCE;
  let data: Record<string, any>;
  let clipartThumbURL;

  switch (source) {
    case StockSources.GETTY_ILLUSTRATION:
      clipartThumbURL = clipartData.u;
      clipartSource = SOURCE.GETTY_ILLUSTRATIONS;
      data = {
        id: clipartData.id ?? uuidv4(),
        artist: clipartData.a as string,
        title: clipartData.t as string,
        col: clipartData.c as string,
        type: clipartData.ty as string,
        tag: getTagForGettyMedia(),
      };
      break;
    case StockSources.PIXABY_ILLUSTRATION:
      clipartThumbURL = clipartData.u;
      clipartSource = SOURCE.PIXABAY_ILLUSTRATIONS;
      data = {
        id: clipartData.id ?? uuidv4(),
        hu: clipartData.hu as string,
      };
      break;
    case StockSources.STORYBLOCK_ILLUSTRATION:
      clipartThumbURL = clipartData.tu;
      clipartSource = SOURCE.STORYBLOCKS_ILLUSTRATIONS;
      data = {
        id: clipartData.id ?? uuidv4(),
        title: clipartData.t as string,
        format: clipartData.defaultFormat as string,
      };
      break;
    default:
      throw new Error(`Invalid clipart source passed: ${source}`);
  }

  return {
    type: GRID_ITEM_TYPE.CLIPART,
    source: clipartSource,
    id: clipartData.id ?? uuidv4(),
    thumbSrc: clipartThumbURL,
    data,
    width: clipartData.w as number,
    height: clipartData.h as number,
    tag: source === StockSources.GETTY_ILLUSTRATION ? getTagForGettyMedia() : undefined,
    hidden: clipartData.hidden ?? undefined,
  };
};

export const getTagForGettyMedia = (): Partial<GridMediaItemTag> | undefined => {
  if (isPremiumUser()) {
    return undefined;
  }
  return {
    type: GridMediaItemTagType.SOURCE,
  };
};

export const iconDataToIconItem = (iconData: GridIconItemBackEndData): GridIconItemStorage => {
  return {
    id: iconData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_STOCK_ICON,
    hashedFilename: iconData.hashedFilename,
  };
};

export const aiIconDataToAiIconItem = (aiIconData: GridAiIconItemBackEndData): GridAiIconItemStorage => {
  return {
    id: aiIconData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_AI_ICON,
    hashedFilename: aiIconData.hashedFilename,
  };
};

export const stillStickerDataToStickerItem = (stillStickerData: GridStillStickerItemBackEndData): GridStillStickerItemStorage => {
  return {
    id: stillStickerData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER,
    hashedFilename: stillStickerData.hashedFilename,
    width: stillStickerData.width !== 0 ? stillStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: stillStickerData.height !== 0 ? stillStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
  };
};

export const aiStickerDataToAiStickerItem = (aiStickerData: GridAiStickerItemBackEndData): GridAiStickerItemStorage => {
  return {
    id: aiStickerData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_AI_STICKER,
    hashedFilename: aiStickerData.hashedFilename,
    width: aiStickerData.width !== 0 ? aiStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: aiStickerData.height !== 0 ? aiStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
  };
};

export const extractedGettyStickerDataToStickerItem = (extractedGettyStickerData: GridStockMediaItemBackendData): GridExtractedGettyStickerItemStorage => {
  return {
    id: extractedGettyStickerData.idPmwStockMedia,
    type: GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER,
    hashedFilename: extractedGettyStickerData.hashedFilename,
    width: extractedGettyStickerData.width !== 0 ? extractedGettyStickerData.width : FALLBACK_GRID_ITEM_WIDTH,
    height: extractedGettyStickerData.height !== 0 ? extractedGettyStickerData.height : FALLBACK_GRID_ITEM_HEIGHT,
    tag: getTagForGettyMedia(),
  };
};

export const imageDataToImageItem = (backEndData: GridStockImageItemBackEndData, source: StockSources): GridStockImageItemStorage => {
  let thumbSrc;
  let id;
  let data = {};
  let src: SOURCE;

  switch (source) {
    case StockSources.GETTY_IMAGE:
      src = SOURCE.GETTY;
      thumbSrc = backEndData.u as string;
      id = backEndData.id as string;
      data = {
        id: backEndData.id as string,
        artist: backEndData.a as string,
        title: backEndData.t as string,
        col: backEndData.c as number,
        type: backEndData.ty as string,
      };
      break;

    case StockSources.PIXABY_IMAGE:
      src = SOURCE.PIXABAY;
      thumbSrc = backEndData.u as string;
      id = backEndData.id as string;
      data = {
        hu: backEndData.hu as string,
      };
      break;

    case StockSources.STORYBLOCK_IMAGE:
      src = SOURCE.STORYBLOCKS;
      thumbSrc = backEndData.tu as string;
      id = backEndData.id as string;
      data = {
        id: backEndData.id as string,
        title: backEndData.t as string,
        format: backEndData.defaultFormat as string,
      };
      break;

    case StockSources.PMW_STOCK_IMAGE:
      src = SOURCE.PMW_IMAGE;
      thumbSrc = getPMWStockImageThumbURL(backEndData.hashedFilename as string);
      id = backEndData.idPmwStockMedia as string;
      data = {
        hashedFilename: backEndData.hashedFilename as string,
        hasTransparency: !!backEndData.has_transparency,
      };
      break;

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return {
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    id,
    thumbSrc,
    source: src,
    data,
    width: backEndData.w as number,
    height: backEndData.h as number,
    tag: source === StockSources.GETTY_IMAGE ? getTagForGettyMedia() : undefined,
  };
};

export const videoDataToVideoItem = (backEndData: GridStockVideoItemBackEndData, source: StockSources): GridStockVideoItemStorage | undefined => {
  if (source && backEndData.u && backEndData.up) {
    switch (source) {
      case StockSources.GETTY_VIDEO:
        return {
          id: backEndData.id as string,
          type: GRID_ITEM_TYPE.STOCK_VIDEO,
          source: SOURCE.GETTY,
          imgThumbSrc: backEndData.u as string,
          videoThumbSrc: backEndData.up as string,
          isUHD: !!backEndData.UHD,
          duration: backEndData.cl as string,
          data: {
            id: backEndData.id as string,
            artist: backEndData.a as string,
            title: backEndData.t as string,
            col: backEndData.c as number,
          },
          width: backEndData.w as number,
          height: backEndData.h as number,
          tag: getTagForGettyMedia(),
        };

      case StockSources.PIXABY_VIDEO:
        return {
          id: backEndData.id as string,
          type: GRID_ITEM_TYPE.STOCK_VIDEO,
          source: SOURCE.PIXABAY,
          imgThumbSrc: backEndData.u as string,
          videoThumbSrc: backEndData.up as string,
          isUHD: false, // pixabay doesn't provide 4k videos
          duration: backEndData.cl as string,
          data: {
            id: backEndData.id as string,
            highResSrc: backEndData.hr as string,
            smallResSrc: backEndData.sr as string,
          },
          width: backEndData.w as number,
          height: backEndData.h as number,
        };

      case StockSources.STORYBLOCK_VIDEO:
        return {
          id: backEndData.id as string,
          type: GRID_ITEM_TYPE.STOCK_VIDEO,
          source: SOURCE.STORYBLOCKS,
          imgThumbSrc: backEndData.u as string,
          videoThumbSrc: backEndData.up as string,
          isUHD: !!backEndData.UHD,
          duration: backEndData.cl as string,
          data: {
            id: backEndData.id as string,
            title: backEndData.t as string,
          },
          width: backEndData.w as number,
          height: backEndData.h as number,
        };

      default:
        throw new Error(`Unhandled source: ${source}`);
    }
  }
  return undefined;
};

export const shapeDataToShapeItem = (shapeData: PMWStockBackendShape): GridShapeItemStorage => {
  return {
    hashedFilename: shapeData.idPMWStockShape,
    type: GRID_ITEM_TYPE.PMW_STOCK_SHAPE,
    id: shapeData.idPmwStockMedia,
  };
};

export const getAllStockSources = (): StockSources[] => {
  const PIXABY_SOURCES: StockSources[] = [StockSources.PIXABY_VIDEO, StockSources.PIXABY_IMAGE, StockSources.PIXABY_ILLUSTRATION];
  const sources: StockSources[] = [
    StockSources.PMW_ICON,
    StockSources.PMW_SHAPE,
    StockSources.PMW_ANIMATED_STICKER,
    StockSources.PMW_STILL_STICKER,
    StockSources.PMW_STOCK_IMAGE,
    StockSources.STORYBLOCK_VIDEO,
    StockSources.STORYBLOCK_IMAGE,
    StockSources.STORYBLOCK_AUDIO,
    StockSources.STORYBLOCK_ILLUSTRATION,
    StockSources.PMW_EXTRACTED_GETTY_STICKER,
    StockSources.PMW_AI_ICON,
    StockSources.PMW_AI_STICKER,
    StockSources.PMW_AI_ANIMATED_STICKER,
  ];
  if (!window.PMW.currentUserOnPageLoad?.isFreelancer) {
    sources.push(...PIXABY_SOURCES);
  }
  if (!isUserStudent(getUserType()) && IS_GETTY_ENABLED) {
    sources.push(StockSources.GETTY_VIDEO, StockSources.GETTY_ILLUSTRATION, StockSources.GETTY_IMAGE);
  }
  return DISABLE_PIXABY_SOURCES
    ? sources.filter((stockSource) => {
        return !PIXABY_SOURCES.includes(stockSource);
      })
    : sources;
};

const handleImageTypeUpload = async (gridItem: GridStockImageItemStorage, shouldLoadDataUrlImage = false): Promise<ElementData> => {
  switch (gridItem.source) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    case SOURCE.PMW_IMAGE:
      return getImageDataForPMWStockImage(gridItem);
    default:
      return doUploadStockImageGridItem(gridItem, shouldLoadDataUrlImage);
  }
};

export const uploadSelectedItem = async (gridItem: GridItemStorage, shouldLoadDataUrlImage = false): Promise<ElementData> => {
  switch (gridItem.type) {
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return handleImageTypeUpload(gridItem as GridStockImageItemStorage, shouldLoadDataUrlImage);
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return uploadStockVideoGridItem(gridItem as GridStockVideoItemStorage);
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return uploadStockAudioGridItem(gridItem as GridStockAudioItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
      return uploadIconGridItem(gridItem as GridIconItemStorage);
    case GRID_ITEM_TYPE.PMW_AI_ICON:
      return uploadAiIconGridItem(gridItem as GridAiIconItemStorage);
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return uploadAiStickerGridItem(gridItem as GridAiStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
      return uploadStillStickerGridItem(gridItem as GridStillStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER:
      return uploadExtractedGettyStickerGridItem(gridItem as GridExtractedGettyStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
      return uploadShapeGridItem(gridItem as GridShapeItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
      return uploadAnimatedStickerGridItem(gridItem as GridAnimatedStickerItemStorage);
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return uploadAiAnimatedStickerGridItem(gridItem as GridAnimatedStickerItemStorage);
    case GRID_ITEM_TYPE.CLIPART:
      return uploadClipartGridItem(gridItem as GridClipartItemStorage);
    default:
      throw new Error(`Unhandled grid item: ${gridItem.type}`);
  }
};

const doUploadStockImageGridItem = async (gridItem: GridItemStorage, shouldLoadDataUrlImage = false): Promise<ElementData> => {
  if (shouldLoadDataUrlImage) {
    let itemToAddData;
    let doesFileExist;
    if (gridItem.source === SOURCE.AI_TEXT_TO_IMAGE) {
      itemToAddData = getGeneratedAiImageDataFromGridItemStorage(gridItem as GridStockImageItemStorage);
      const userImageHashedFilename = await getUserImageHashedFilenameForAiImage(Number(gridItem.id));
      doesFileExist =
        !!userImageHashedFilename && (await doesPublicFileExistAsync(getUrlForImageItemScreenTier(userImageHashedFilename, itemToAddData.source, ScreenTier.SMALL, false)));
    } else {
      itemToAddData = getStockImageDataFromGridStockImageItemStorage(gridItem as GridStockImageItemStorage);
      doesFileExist = await doesPublicFileExistAsync(getUrlForImageItemScreenTier(itemToAddData.hashedFilename, itemToAddData.source, ScreenTier.SMALL, false));
    }
    if (!doesFileExist) {
      const tempUploadingImageUID = uuidv4();

      const imageURL = gridItem.source === SOURCE.AI_TEXT_TO_IMAGE ? (gridItem.highResSrc as string) : (gridItem.thumbSrc as string);
      const dataurl = await imageUrlToDataUrl(imageURL);
      if (dataurl) {
        void uploadStockImageGridItem(gridItem as GridStockImageItemStorage, tempUploadingImageUID);

        return {
          uid: tempUploadingImageUID,
          dataUrl: dataurl,
          type: itemToAddData.type,
          source: itemToAddData.source,
          stockId: gridItem.source === SOURCE.AI_TEXT_TO_IMAGE ? undefined : gridItem.id,
        } as TempImageData;
      }
      return uploadStockImageGridItem(gridItem as GridStockImageItemStorage);
    } else if (doesFileExist && gridItem.source !== SOURCE.AI_TEXT_TO_IMAGE) {
      try {
        const data = (await window.PMW.readLocal('posterimage/getUserImage', {
          hashedId: getHashedFilenameForStockAsset(gridItem.source as SOURCE, gridItem.id),
        })) as UserImageVOResponse;
        return getImageDataFromUserImageVO(data);
      } catch {
        return uploadStockImageGridItem(gridItem as GridStockImageItemStorage);
      }
    }
  }
  return uploadStockImageGridItem(gridItem as GridStockImageItemStorage);
};

export const getUserImageHashedFilenameForAiImage = async (aiGeneratedImageId: number): Promise<string> => {
  return (await window.PMW.readLocal('stockmedia/getUserImageHashedFilenameForAiImage', {
    aiGeneratedImageId,
  })) as Promise<string>;
};

const uploadStockImageGridItem = async (gridItem: GridStockImageItemStorage, tempUploadingImageUID?: string): Promise<ImageData> => {
  const {source} = gridItem;
  let data: UserImageVOResponse;

  switch (source as SOURCE) {
    case SOURCE.GETTY:
      data = await runUserImageUploadJob(() =>
        gettyImageUploadV2({
          imageId: gridItem.data.id as string,
          type: gridItem.data.type as string,
          title: gridItem.data.title as string,
          artist: gridItem.data.artist as string,
          collection: gridItem.data.col as number,
          tempUploadingImageUID,
        })
      );
      break;

    case SOURCE.PIXABAY:
      data = await runUserImageUploadJob(() =>
        pixabayImageUploadV2({
          url: gridItem.data.hu as string,
          ...(gridItem.id ? {imageId: gridItem.id} : {}),
          tempUploadingImageUID,
        })
      );
      break;

    case SOURCE.STORYBLOCKS:
      data = await runUserImageUploadJob(() =>
        storyBlocksImageUploadV2({
          imageId: gridItem.data.id as string,
          tempUploadingImageUID,
        })
      );
      break;

    case SOURCE.AI_TEXT_TO_IMAGE:
      data = (await window.PMW.pollingAjaxCallAsync(
        textToImageUpload.bind(null, {
          userId: gridItem.data.uploaderId ? Number(gridItem.data.uploaderId) : undefined,
          aiGeneratedImageId: gridItem.data.id as number,
          tempUploadingImageUID,
        }),
        checkGeneratedImageUploadStatus
      )) as UserImageVOResponse;
      break;

    case SOURCE.PMW_IMAGE:
      return getImageDataForPMWStockImage(gridItem);

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return getImageDataFromUserImageVO(data);
};

const checkGeneratedImageUploadStatus = async (aiGeneratedImageId: number): Promise<any> => {
  return window.PMW.readLocal('stockmedia/getGeneratedImageStatus', {
    aiGeneratedImageId,
  });
};

const uploadStockVideoGridItem = async (gridItem: GridStockVideoItemStorage): Promise<VideoData> => {
  const {source} = gridItem;
  let data: UserVideoVOResponse;

  switch (source as SOURCE) {
    case SOURCE.GETTY:
      data = await runUserVideoUploadJob(() =>
        gettyVideoUploadV2({
          videoId: gridItem.data.id as string,
          title: gridItem.data.title as string,
          artist: gridItem.data.artist as string,
          collection: gridItem.data.col as number,
        })
      );
      break;

    case SOURCE.PIXABAY:
      data = await runUserVideoUploadJob(() =>
        pixabayVideoUploadV2({
          videoId: gridItem.data.id as string,
          videoHighRes: gridItem.data.highResSrc as string,
          videoSmallRes: gridItem.data.smallResSrc as string,
        })
      );
      break;

    case SOURCE.STORYBLOCKS:
      data = await runUserVideoUploadJob(() =>
        storyBlocksVideoUploadV2({
          videoId: gridItem.data.id as string,
        })
      );
      break;

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return getVideoDataFromUserVideoVO(data);
};

const uploadStockAudioGridItem = async (gridItem: GridStockAudioItemStorage): Promise<AudioData> => {
  const data = await runUserAudioUploadJob(() =>
    storyBlocksAudioUploadV2({
      audioId: gridItem.data.id as string,
      title: gridItem.name,
    })
  );

  return getAudioDataFromUserAudioVO(data);
};

const uploadIconGridItem = (gridItem: GridIconItemStorage): PMWIconData => {
  return {
    type: ElementDataType.ICON,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadAiIconGridItem = (gridItem: GridAiIconItemStorage): PMWAiIconData => {
  return {
    type: ElementDataType.AI_ICON,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadAiStickerGridItem = (gridItem: GridAiStickerItemStorage): PMWAiStickerData => {
  return {
    type: ElementDataType.AI_STICKER,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadStillStickerGridItem = (gridItem: GridStillStickerItemStorage): PMWStillStickerData => {
  return {
    type: ElementDataType.STILL_STICKER,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadExtractedGettyStickerGridItem = (gridItem: GridExtractedGettyStickerItemStorage): PMWExtractedGettyStickerData => {
  return {
    type: ElementDataType.EXTRACTED_GETTY_STICKER,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadShapeGridItem = (gridItem: GridShapeItemStorage): PMWShapeData => {
  return {
    type: ElementDataType.PMW_SHAPE,
    hashedFilename: gridItem.hashedFilename,
  };
};

const uploadAnimatedStickerGridItem = (gridItem: GridAnimatedStickerItemStorage): AnimatedStickerData => {
  return {
    type: ElementDataType.ANIMATED_STICKER,
    hashedFilename: gridItem.hashedFilename,
    frameRate: gridItem.frameRate,
    duration: gridItem.duration,
    highResAnimatedSprite: gridItem.highResAnimatedSprite,
    screenAnimatedSprite: gridItem.screenAnimatedSprite,
    stickerSource: StickerItemSource.PMW_ANIMATED_STICKER,
  };
};

const uploadAiAnimatedStickerGridItem = (gridItem: GridAnimatedStickerItemStorage): AiAnimatedStickerData => {
  return {
    type: ElementDataType.AI_ANIMATED_STICKER,
    hashedFilename: gridItem.hashedFilename,
    frameRate: gridItem.frameRate,
    duration: gridItem.duration,
    highResAnimatedSprite: gridItem.highResAnimatedSprite,
    screenAnimatedSprite: gridItem.screenAnimatedSprite,
    stickerSource: StickerItemSource.PMW_AI_ANIMATED_STICKER,
  };
};

export const getDefaultSelectedFilterForTab = (tab: StockPanelTabs): SelectedFilter[] => {
  switch (tab) {
    case StockPanelTabs.ALL:
    case StockPanelTabs.VIDEOS:
    case StockPanelTabs.PHOTOS:
    case StockPanelTabs.STICKERS:
      return [];
    case StockPanelTabs.AUDIOS:
    case StockPanelTabs.ICONS:
    case StockPanelTabs.TEXT_TO_IMAGE:
    case StockPanelTabs.SHAPES:
      return [];
    default:
      throw new Error(`Unhandled tab: ${tab}`);
  }
};

const uploadClipartGridItem = async (gridItem: GridClipartItemStorage): Promise<ImageData> => {
  const {source} = gridItem;
  let data: UserImageVOResponse;

  switch (source) {
    case SOURCE.PIXABAY_ILLUSTRATIONS:
      data = await runUserImageUploadJob(() =>
        pixabayImageUploadV2({
          url: gridItem.data.hu as string,
          ...(gridItem.data.id ? {imageId: String(gridItem.data.id)} : {}),
        })
      );
      break;

    case SOURCE.STORYBLOCKS_ILLUSTRATIONS:
      data = await runUserImageUploadJob(() =>
        storyBlocksImageUploadV2({
          imageId: gridItem.data.id as string,
        })
      );
      break;

    case SOURCE.GETTY_ILLUSTRATIONS:
      data = await runUserImageUploadJob(() =>
        gettyImageUploadV2({
          imageId: gridItem.data.id as string,
          type: gridItem.data.type as string,
          title: gridItem.data.title as string,
          artist: gridItem.data.artist as string,
          collection: gridItem.data.col as number,
        })
      );
      break;

    default:
      throw new Error(`Unhandled source: ${source}`);
  }

  return getImageDataFromUserImageVO(data);
};

export const getHasUserSeenMobileVideoPreview = (): Promise<boolean> => {
  return window.PMW.readLocal('stockmedia/hasUserSeenMobileVideoPreviewMessage');
};

export const markUserHasSeenMobileVideoPreview = (): Promise<boolean> => {
  return window.PMW.readLocal('stockmedia/markUserHasSeenMobileVideoPreviewMessage');
};

export const getSearchBarPlaceholderText = (selectedTab?: StockPanelTabs): string => {
  if (selectedTab) {
    switch (selectedTab) {
      case StockPanelTabs.PHOTOS:
        return window.i18next.t('pmwjs_search_photos');
      case StockPanelTabs.VIDEOS:
        return window.i18next.t('pmwjs_search_videos');
      case StockPanelTabs.SHAPES:
        return window.i18next.t('pmwjs_search_shapes');
      case StockPanelTabs.STICKERS:
        return window.i18next.t('pmwjs_search_stickers');
      case StockPanelTabs.ICONS:
        return window.i18next.t('pmwjs_search_icons');
      case StockPanelTabs.AUDIOS:
        return window.i18next.t('pmwjs_search_audios');
      case StockPanelTabs.TEXT_TO_IMAGE:
        return window.i18next.t('pmwjs_prompt');
      default:
        throw new Error(`Unhandled stock tab: ${selectedTab}`);
    }
  }
  return window.i18next.t('pmwjs_explore_photos_videos_and_more');
};

export const DEFAULT_TABS = [
  StockPanelTabs.ALL,
  StockPanelTabs.STICKERS,
  StockPanelTabs.SHAPES,
  StockPanelTabs.PHOTOS,
  StockPanelTabs.VIDEOS,
  StockPanelTabs.ICONS,
  StockPanelTabs.AUDIOS,
];

export const getStockTabsOrder = (): StockPanelTabs[] => {
  return DEFAULT_TABS;
};

// During a search the internal PMW types (OpenSearch-backed) lead, in this fixed order; the externally
// sourced types (Getty / Storyblocks) are pushed to the end. See plans/phase-28.md.
const SEARCH_PRIORITY_TABS = [StockPanelTabs.SHAPES, StockPanelTabs.STICKERS, StockPanelTabs.ICONS];
const SEARCH_TRAILING_TABS = [StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS, StockPanelTabs.AUDIOS];

/**
 * Orders the carousels shown while a search is active: internal priority tabs first (Shapes, Stickers,
 * Icons), then any tab not in either group, then the externally sourced tabs (Photos, Videos, Audios).
 * Only tabs present in `tabsToShow` are kept, so it is safe to pass a custom subset.
 */
export const getSearchTabOrder = (tabsToShow: StockPanelTabs[]): StockPanelTabs[] => {
  const grouped = new Set([...SEARCH_PRIORITY_TABS, ...SEARCH_TRAILING_TABS]);
  const present = (tabs: StockPanelTabs[]): StockPanelTabs[] => tabs.filter((tab) => tabsToShow.includes(tab));
  const ungrouped = tabsToShow.filter((tab) => !grouped.has(tab));
  return [...present(SEARCH_PRIORITY_TABS), ...ungrouped, ...present(SEARCH_TRAILING_TABS)];
};

export const shouldShowShowMoreCTA = (carouselType: StockCarouselType): boolean => {
  switch (carouselType) {
    case StockCarouselType.RECENTS:
      return false;
    case StockCarouselType.AI:
    case StockCarouselType.ICONS:
    case StockCarouselType.AUDIOS:
    case StockCarouselType.PHOTOS:
    case StockCarouselType.SHAPES:
    case StockCarouselType.STICKERS:
    case StockCarouselType.VIDEOS:
      return true;
    default:
      throw new Error(`Unhanled carousel type: ${carouselType}`);
  }
};

export const getTestIdForCarouselType = (carouselType: StockCarouselType): PosterEditorTestId | undefined => {
  switch (carouselType) {
    case StockCarouselType.SHAPES:
      return PosterEditorTestId.STOCK_PANEL_CAROUSEL_SHAPES;
    default:
      return undefined;
  }
};

export const getCarouselTitleText = (carouselType: StockCarouselType): string => {
  switch (carouselType) {
    case StockCarouselType.RECENTS:
      return window.i18next.t('pmwjs_recent');
    case StockCarouselType.PHOTOS:
      return window.i18next.t('pmwjs_photos_2');
    case StockCarouselType.STICKERS:
      return window.i18next.t('pmwjs_stickers');
    case StockCarouselType.SHAPES:
      return window.i18next.t('pmwjs_shapes');
    case StockCarouselType.VIDEOS:
      return window.i18next.t('pmwjs_videos_2');
    case StockCarouselType.AUDIOS:
      return window.i18next.t('pmwjs_audios_2');
    case StockCarouselType.ICONS:
      return window.i18next.t('pmwjs_icons');
    case StockCarouselType.AI:
      return window.i18next.t('pmwjs_ai');
    default:
      throw new Error(`Unhandled carousel type for getCarouselTitleText: ${carouselType}`);
  }
};

export const getBottomPills = (selectedTab: string, callback: (id: string) => void): PanelBottomPill[] => {
  return [
    {
      id: StockPanelTabs.PHOTOS,
      text: window.i18next.t('pmwjs_photos_2'),
      icon: 'icon-image',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.PHOTOS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.TEXT_TO_IMAGE,
      text: window.i18next.t('pmwjs_ai_images'),
      icon: 'icon-ai-image',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.TEXT_TO_IMAGE,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.SHAPES,
      text: window.i18next.t('pmwjs_shapes'),
      icon: 'icon-elements',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.SHAPES,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.STICKERS,
      text: window.i18next.t('pmwjs_stickers'),
      icon: 'icon-sticker',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.STICKERS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.ICONS,
      text: window.i18next.t('pmwjs_icons'),
      icon: 'icon-elements',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.ICONS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.VIDEOS,
      text: window.i18next.t('pmwjs_videos_2'),
      icon: 'icon-video',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.VIDEOS,
      onClick(id: string): void {
        callback(id);
      },
    },
    {
      id: StockPanelTabs.AUDIOS,
      text: window.i18next.t('pmwjs_audios_2'),
      icon: 'icon-audio',
      isSelected: (selectedTab as StockPanelTabs) === StockPanelTabs.AUDIOS,
      onClick(id: string): void {
        callback(id);
      },
    },
  ];
};

export const getOptionsForStockGetter = (selectedFilters: SelectedFilter[], selectedTab: string, audioFilterIDs: (StockButtonFilters | string)[]): StockGetterOptions => {
  const stockGetterParam: StockGetterOptions = {};
  if (selectedFilters.length > 0) {
    if ((selectedTab as StockPanelTabs) === StockPanelTabs.AUDIOS) {
      const audioFilters: Record<StockButtonFilters, string[]> | Record<string, string[]> = {};
      selectedFilters.forEach((selectedFilter) => {
        const isPresent = audioFilterIDs.includes(selectedFilter.filter);
        if (isPresent && selectedFilter.subFilter) {
          audioFilters[selectedFilter.filter as StockButtonFilters] = [selectedFilter.subFilter];
        }
      });
      stockGetterParam.audioFilters = audioFilters;
    } else if ((selectedTab as StockPanelTabs) === StockPanelTabs.VIDEOS) {
      stockGetterParam.only4KVideos = selectedFilters.some((filters) => {
        return (filters.filter as StockButtonFilters) === StockButtonFilters.QUALITY && filters.subFilter === UHD_APPLIED_VIDEO_FILTER_ID;
      })
        ? true
        : undefined;
    } else if ((selectedTab as StockPanelTabs) === StockPanelTabs.ICONS) {
      selectedFilters.forEach((selectedFilter) => {
        if (selectedFilter.subFilter) {
          stockGetterParam.iconCategories = ICON_FILTERS_TO_CATEGORY_MAPPING[selectedFilter.subFilter];
        }
      });
    } else if ((selectedTab as StockPanelTabs) === StockPanelTabs.SHAPES) {
      selectedFilters.forEach((selectedFilter) => {
        if (selectedFilter.subFilter) {
          stockGetterParam.shapeCategories = SHAPE_FILTERS_TO_CATEGORY_MAPPING[selectedFilter.subFilter];
        }
      });
    }
    selectedFilters.forEach((selectedFilter) => {
      if (selectedFilter.filter === StockButtonFilters.SEASONAL.valueOf()) {
        stockGetterParam.seasonalSearch = selectedFilter.subFilter ?? '';
      }
    });
    const orientationFilter = selectedFilters.find((filter) => filter.filter === StockButtonFilters.ORIENTATION.valueOf());
    if (orientationFilter?.subFilter) {
      stockGetterParam.orientation = orientationFilter.subFilter;
    }
  }
  return stockGetterParam;
};

export const getCarouselDataForShapeItem = (shapeData: PMWStockBackendShape): CarouselItemData => {
  return {
    gridItemData: shapeDataToShapeItem(shapeData),
    height: FALLBACK_GRID_ITEM_HEIGHT,
    width: FALLBACK_GRID_ITEM_WIDTH,
  };
};

export const getCarouselDataForIconItem = (iconData: PMWStockBackendIcon): CarouselItemData => {
  return {
    gridItemData: iconDataToIconItem({...iconData, sortOrder: iconData.adminScore, isHidden: iconData.isHidden !== 1}),
    height: FALLBACK_GRID_ITEM_HEIGHT,
    width: FALLBACK_GRID_ITEM_WIDTH,
  };
};

export const getCarouselDataForAiIconItem = (aiIconData: PMWStockBackendAiIcon): CarouselItemData => {
  return {
    gridItemData: aiIconDataToAiIconItem({...aiIconData, sortOrder: aiIconData.adminScore, isHidden: aiIconData.isHidden !== 1}),
    height: FALLBACK_GRID_ITEM_HEIGHT,
    width: FALLBACK_GRID_ITEM_WIDTH,
  };
};

export const getCarouselDataForStillStickerItem = (stillStickerData: PMWStockBackendStillSticker): CarouselItemData => {
  return {
    gridItemData: stillStickerDataToStickerItem({...stillStickerData, sortOrder: stillStickerData.adminScore, isHidden: stillStickerData.isHidden !== 1}),
    height: stillStickerData.height,
    width: stillStickerData.width,
  };
};

export const getCarouselDataForAiStickerItem = (aiStickerData: PMWStockBackendAiSticker): CarouselItemData => {
  return {
    gridItemData: aiStickerDataToAiStickerItem({...aiStickerData, sortOrder: aiStickerData.adminScore, isHidden: aiStickerData.isHidden !== 1}),
    height: aiStickerData.height,
    width: aiStickerData.width,
  };
};

export const getCarouselDataForExtractedGettyStickerItem = (extractedGettyStickerData: PMWStockBackendExtractedGettySticker): CarouselItemData => {
  return {
    gridItemData: extractedGettyStickerDataToStickerItem({
      ...extractedGettyStickerData,
      sortOrder: extractedGettyStickerData.adminScore,
      isHidden: extractedGettyStickerData.isHidden !== 1,
    }),
    height: extractedGettyStickerData.height,
    width: extractedGettyStickerData.width,
  };
};

export const getCarouselDataForStickerItem = (stickerData: PMWStockBackendAnimatedSticker): CarouselItemData => {
  return {
    gridItemData: animatedStickerDataToAnimatedStickerItem(stickerData),
    height: stickerData.height,
    width: stickerData.width,
  };
};

export const getCarouselDataForAiAnimatedStickerItem = (stickerData: PMWStockBackendAiAnimatedSticker): CarouselItemData => {
  return {
    gridItemData: aiAnimatedStickerDataToAiAnimatedStickerItem(stickerData),
    height: stickerData.height,
    width: stickerData.width,
  };
};

export const getCarouselDataForAudioItem = (audioData: GridStockAudioItemBackEndData, height: number): CarouselItemData => {
  return {
    gridItemData: {
      ...audioDataToAudioItem(audioData),
      gridItemHeight: height,
    } as GridStockAudioItemStorage,
    width: STOCK_AUDIO_GRID_ITEM_WIDTH,
    height,
    hasFixedWidth: true,
  };
};

export const getCarouselDataForImageItem = (imageData: GridStockImageItemBackEndData, source: StockSources): CarouselItemData => {
  return {
    gridItemData: imageDataToImageItem(imageData, source),
    width: Number(imageData.w),
    height: Number(imageData.h),
  };
};

export const getCarouselDataForClipartItem = (clipartData: GridClipartItemBackEndData, source: StockSources): CarouselItemData => {
  return {
    gridItemData: clipartDataToClipartItem(clipartData, source),
    width: Number(clipartData.w) || FALLBACK_GRID_ITEM_WIDTH,
    height: Number(clipartData.h) || FALLBACK_GRID_ITEM_HEIGHT,
  };
};

export const getCarouselDataForVideoItem = (videoData: GridStockVideoItemBackEndData, source: StockSources): CarouselItemData | undefined => {
  const gridItem = videoDataToVideoItem(videoData, source);

  if (gridItem) {
    return {
      gridItemData: gridItem,
      width: Number(videoData.w),
      height: Number(videoData.h),
    };
  }

  return undefined;
};

export const getGridID = (tab: StockPanelTabs): StockGridIDs => {
  switch (tab) {
    case StockPanelTabs.PHOTOS:
      return StockGridIDs.PHOTOS;
    case StockPanelTabs.VIDEOS:
      return StockGridIDs.VIDEOS;
    case StockPanelTabs.SHAPES:
      return StockGridIDs.SHAPES;
    case StockPanelTabs.STICKERS:
      return StockGridIDs.STICKERS;
    case StockPanelTabs.ICONS:
      return StockGridIDs.ICONS;
    case StockPanelTabs.AUDIOS:
      return StockGridIDs.AUDIOS;
    case StockPanelTabs.TEXT_TO_IMAGE:
      return StockGridIDs.TEXT_TO_IMAGE;
    default:
      throw new Error(`Unhandled selected tab: ${tab} for getting grid id`);
  }
};

export const getTabItem = (tab: StockPanelTabs, textClasses?: string): TabItemContentStorage => {
  return {
    tabKey: tab,
    text: getTabDisplayText(tab),
    textClassName: textClasses,
  };
};

const getTabDisplayText = (tab: StockPanelTabs): string => {
  switch (tab) {
    case StockPanelTabs.PHOTOS:
      return window.i18next.t('pmwjs_photos_2');
    case StockPanelTabs.SHAPES:
      return window.i18next.t('pmwjs_shapes');
    case StockPanelTabs.STICKERS:
      return window.i18next.t('pmwjs_stickers');
    case StockPanelTabs.ICONS:
      return window.i18next.t('pmwjs_icons');
    case StockPanelTabs.VIDEOS:
      return window.i18next.t('pmwjs_videos_2');
    case StockPanelTabs.AUDIOS:
      return window.i18next.t('pmwjs_audios_2');
    case StockPanelTabs.TEXT_TO_IMAGE:
      return window.i18next.t('pmwjs_ai_images');
    default:
      throw new Error(`Unhandled tab type for getTabDisplayText: ${tab}`);
  }
};

export const getStockFilterForAudioFilterBackendData = (backendData: AudioCategoryBackendData): StockFilter => {
  return {
    id: backendData.id as StockFilterID,
    type: StockFilterTypes.DROPDOWN,
    name: AUDIO_CATEGORY_ID_TO_STOCK_BUTTON_FILTER[backendData.id],
    subFilters: backendData.subcategories.map((subcategoryData) => {
      return {
        id: `${subcategoryData.id}`,
        text: subcategoryData.displayName,
      };
    }),
  };
};

export const getSelectedSubfilterIDForStockFilter = (stockFilter: StockFilter, selectedFilters: SelectedFilter[]): string | undefined => {
  if (selectedFilters.length > 0) {
    const selectedFilter = selectedFilters.find((filterSelected) => {
      return (filterSelected.filter as StockFilterID) === stockFilter.id;
    });
    if (selectedFilter) {
      const selectedSubfilter = stockFilter.subFilters.find((subfilter) => {
        return subfilter.id === selectedFilter.subFilter;
      });
      return selectedSubfilter?.id;
    }
  }
  return undefined;
};

const doesSubFilterRefMatch = (ref: StockSubFilterRef, filterID: string, subFilterID: string | null): boolean => {
  return ref.filter.valueOf() === filterID && ref.subFilter === subFilterID;
};

const getSubFiltersExclusiveWith = (tab: StockPanelTabs, filterID: string, subFilterID: string): StockSubFilterRef[] => {
  const pairs = MUTUALLY_EXCLUSIVE_SUBFILTERS[tab] ?? [];
  return pairs
    .filter(([first, second]) => {
      return doesSubFilterRefMatch(first, filterID, subFilterID) || doesSubFilterRefMatch(second, filterID, subFilterID);
    })
    .map(([first, second]) => {
      return doesSubFilterRefMatch(first, filterID, subFilterID) ? second : first;
    });
};

export const removeSubFiltersExclusiveWith = (tab: StockPanelTabs, filterID: string, subFilterID: string, selectedFilters: SelectedFilter[]): SelectedFilter[] => {
  const exclusiveRefs = getSubFiltersExclusiveWith(tab, filterID, subFilterID);
  return selectedFilters.filter((selected) => {
    return !exclusiveRefs.some((ref) => {
      return doesSubFilterRefMatch(ref, selected.filter, selected.subFilter);
    });
  });
};

const getTagForMediaItem = (source: SOURCE): Partial<GridMediaItemTag> | undefined => {
  switch (source) {
    case SOURCE.GETTY_ILLUSTRATIONS:
    case SOURCE.GETTY:
      return getTagForGettyMedia();
    default:
      return undefined;
  }
};

const gridAudioItemBackendDataToAudioItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStockAudioItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.STOCK_AUDIO,
    name: gridItemBackendData.name as string,
    data: gridItemBackendData.data as Record<string, any>,
    id: gridItemBackendData.id as string,
  };
};

const gridShapeItemBackendDataToShapeItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridShapeItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_SHAPE,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridClipartItemBackendDataToClipartItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridClipartItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.CLIPART,
    thumbSrc: gridItemBackendData.thumbSrc as string,
    source: gridItemBackendData.source as SOURCE,
    id: gridItemBackendData.id as string,
    data: gridItemBackendData.data as Record<string, any>,
    tag: getTagForMediaItem(gridItemBackendData.source as SOURCE),
  };
};

const gridStockVideoItemBackendDataToStockVideoItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStockVideoItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.STOCK_VIDEO,
    source: gridItemBackendData.source as string,
    data: gridItemBackendData.data as Record<string, any>,
    id: gridItemBackendData.id as string,
    isUHD: !!gridItemBackendData.isUHD,
    tag: getTagForMediaItem(gridItemBackendData.source as SOURCE),
  };
};

const gridAnimatedStickerItemBackendDataToAnimatedStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAnimatedStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    duration: Number(gridItemBackendData.duration),
    frameRate: Number(gridItemBackendData.frameRate),
    id: gridItemBackendData.id as string,
    highResAnimatedSprite: gridItemBackendData.highResAnimatedSprite as AnimatedSprite,
    screenAnimatedSprite: gridItemBackendData.screenAnimatedSprite as AnimatedSprite,
  };
};

const gridAiAnimatedStickerItemBackendDataToAiAnimatedStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAnimatedStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    duration: Number(gridItemBackendData.duration),
    frameRate: Number(gridItemBackendData.frameRate),
    id: gridItemBackendData.id as string,
    highResAnimatedSprite: gridItemBackendData.highResAnimatedSprite as AnimatedSprite,
    screenAnimatedSprite: gridItemBackendData.screenAnimatedSprite as AnimatedSprite,
  };
};

const gridStockImageItemBackendDataToStockImageItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStockImageItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    thumbSrc: gridItemBackendData.thumbSrc as string,
    source: gridItemBackendData.source as SOURCE,
    id: gridItemBackendData.id as string,
    data: gridItemBackendData.data as Record<string, any>,
    tag: getTagForMediaItem(gridItemBackendData.source as SOURCE),
  };
};

const gridIconItemBackendDataToStockIconItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridIconItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_ICON,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridAiIconItemBackendDataToStockAiIconItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAiIconItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_AI_ICON,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridStillStickerItemBackendDataToStockStillStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridStillStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridAiStickerItemBackendDataToStockAiStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridAiStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_AI_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

const gridExtractedGettyStickerItemBackendDataToStockExtractedGettyStickerItem = (gridItemBackendData: GridItemBaseStorageBackendData): GridExtractedGettyStickerItemStorage => {
  return {
    ...gridItemBackendData,
    type: GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER,
    hashedFilename: gridItemBackendData.hashedFilename as string,
    id: gridItemBackendData.id as string,
  };
};

export const getGridItemForBackendData = (gridItemBackendData: GridItemBaseStorageBackendData): GridItemBaseStorage => {
  switch (gridItemBackendData.type as GRID_ITEM_TYPE) {
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return gridAudioItemBackendDataToAudioItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
    case GRID_ITEM_TYPE.SHAPE:
      return gridShapeItemBackendDataToShapeItem(gridItemBackendData);
    case GRID_ITEM_TYPE.CLIPART:
      return gridClipartItemBackendDataToClipartItem(gridItemBackendData);
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return gridStockVideoItemBackendDataToStockVideoItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
    case GRID_ITEM_TYPE.ANIMATED_STICKER:
      return gridAnimatedStickerItemBackendDataToAnimatedStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return gridAiAnimatedStickerItemBackendDataToAiAnimatedStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return gridStockImageItemBackendDataToStockImageItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_AI_ICON:
      return gridAiIconItemBackendDataToStockAiIconItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
    case GRID_ITEM_TYPE.ICON:
      return gridIconItemBackendDataToStockIconItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return gridAiStickerItemBackendDataToStockAiStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
    case GRID_ITEM_TYPE.STILL_STICKER:
      return gridStillStickerItemBackendDataToStockStillStickerItem(gridItemBackendData);
    case GRID_ITEM_TYPE.PMW_STOCK_EXTRACTED_GETTY_STICKER:
      return gridExtractedGettyStickerItemBackendDataToStockExtractedGettyStickerItem(gridItemBackendData);
    default:
      throw new Error(`Unhandled type: ${gridItemBackendData.type as string}`);
  }
};


export const trackAiToolMenuOpenFromMediaDrawer = (key: AiPanelKey | null): void => {
  if (key === 'ai-images') {
    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.MEDIA_DRAWER});
  } else if (key === 'ai-videos') {
    trackPosterBuilderGA4Events(GA4EventName.AI_VIDEO_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.MEDIA_DRAWER});
  } else if (key === 'ai-voice') {
    trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_MENU_OPEN, {[GA4EventParamName.SOURCE]: AiMenuOpenSource.MEDIA_DRAWER});
  }
};

export const getGATrackingSourceFromMediaSource = (src: SOURCE): string => {
  switch (src) {
    case SOURCE.GETTY:
    case SOURCE.GETTY_ILLUSTRATIONS:
      return capitalize(SOURCE.GETTY);
    case SOURCE.STORYBLOCKS:
    case SOURCE.STORYBLOCKS_ILLUSTRATIONS:
      return capitalize(SOURCE.STORYBLOCKS);
    case SOURCE.PIXABAY:
    case SOURCE.PIXABAY_ILLUSTRATIONS:
      return capitalize(SOURCE.PIXABAY);
    case SOURCE.PMW_IMAGE:
      return capitalize(SOURCE.PMW_IMAGE);
    default:
      return SOURCE.PMW.toUpperCase();
  }
};

export const getGATrackingTypeFromMediaType = (gridItemType: GRID_ITEM_TYPE): string => {
  switch (gridItemType) {
    case GRID_ITEM_TYPE.STOCK_IMAGE:
      return 'Photo';
    case GRID_ITEM_TYPE.STOCK_VIDEO:
      return 'Video';
    case GRID_ITEM_TYPE.STOCK_AUDIO:
      return 'Audio';
    case GRID_ITEM_TYPE.CLIPART:
    case GRID_ITEM_TYPE.PMW_STOCK_STILL_STICKER:
      return 'StillSticker';
    case GRID_ITEM_TYPE.PMW_AI_STICKER:
      return 'AiStillSticker';
    case GRID_ITEM_TYPE.PMW_STOCK_ANIMATED_STICKER:
      return 'AnimatedSticker';
    case GRID_ITEM_TYPE.PMW_STOCK_AI_ANIMATED_STICKER:
      return 'AiAnimatedSticker';
    case GRID_ITEM_TYPE.PMW_STOCK_SHAPE:
      return 'Shape';
    case GRID_ITEM_TYPE.PMW_STOCK_ICON:
      return 'Icon';
    default:
      return '';
  }
};

export const getGATrackingSearchTerm = (tab: StockPanelTabs): string => {
  switch (tab) {
    case StockPanelTabs.ICONS:
      return 'Icon';
    case StockPanelTabs.PHOTOS:
      return 'Photo';
    case StockPanelTabs.VIDEOS:
      return 'Video';
    case StockPanelTabs.AUDIOS:
      return 'Audio';
    case StockPanelTabs.SHAPES:
      return 'Shape';
    case StockPanelTabs.STICKERS:
      return 'Sticker';
    default:
      return capitalize(tab);
  }
};

export const getGATrackingFilterTerm = (filters: SelectedFilter[]): string => {
  for (const filter of filters) {
    if (filter.filter === StockButtonFilters.ANIMATION.valueOf() && filter.subFilter === AnimationSubFilter.ANIMATED) {
      return 'PMWAnimated';
    }
    if (filter.filter === StockButtonFilters.ANIMATION.valueOf() && filter.subFilter === AnimationSubFilter.STILL) {
      return 'Still';
    }
  }
  return '';
};

const shouldShowHalloweenPlaceholder = (): boolean => {
  const month = new Date().getMonth();
  return month === 8 || month === 9;
};

export const getPlaceholderTextForAiImagePrompt = (): string => {
  const AI_IMAGES_WHIMSICAL_TEXTS = [
    window.i18next.t('pmwjs_ai_images_whimsical_1'),
    window.i18next.t('pmwjs_ai_images_whimsical_2'),
    window.i18next.t('pmwjs_ai_images_whimsical_3'),
    window.i18next.t('pmwjs_ai_images_whimsical_4'),
    window.i18next.t('pmwjs_ai_images_whimsical_5'),
  ];

  const AI_IMAGES_NON_WHIMSICAL_TEXTS = [
    window.i18next.t('pmwjs_ai_images_non_whimsical_1'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_2'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_3'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_4'),
    window.i18next.t('pmwjs_ai_images_non_whimsical_5'),
  ];

  const AI_IMAGES_HALLOWEEN_TEXTS = [
    window.i18next.t('pmwjs_ai_images_halloween_1'),
    window.i18next.t('pmwjs_ai_images_halloween_2'),
    window.i18next.t('pmwjs_ai_images_halloween_3'),
    window.i18next.t('pmwjs_ai_images_halloween_4'),
    window.i18next.t('pmwjs_ai_images_halloween_5'),
  ];

  let index = Math.floor(Math.random() * (AI_IMAGES_NON_WHIMSICAL_TEXTS.length + AI_IMAGES_WHIMSICAL_TEXTS.length));
  if (shouldShowHalloweenPlaceholder()) {
    index = Math.floor(Math.random() * AI_IMAGES_HALLOWEEN_TEXTS.length);
    return AI_IMAGES_HALLOWEEN_TEXTS[index];
  }
  return [...AI_IMAGES_NON_WHIMSICAL_TEXTS, ...AI_IMAGES_WHIMSICAL_TEXTS][index];
};

/**
 * Reads the AI-carousel routing marker off a grid item. Items added through the aggregated "AI" carousel carry the
 * type of AI content they represent and the originating generated-asset id, so the add path can route a video to
 * uploadGeneratedAiVideo and a voice to addGeneratedVoiceToDesign instead of the generic stock upload.
 * `aiContentType` / `aiId` live on the storage's open index signature, so they read back as `unknown` and are validated here.
 */
export const getAiCarouselItemMeta = (item: GridItemStorage): AiCarouselItemMeta | undefined => {
  const aiId: unknown = item.aiId;
  if (typeof aiId !== 'number') {
    return undefined;
  }
  const aiContentType: unknown = item.aiContentType;
  switch (aiContentType) {
    case AiCarouselContentType.IMAGES:
      return {aiContentType: AiCarouselContentType.IMAGES, aiId};
    case AiCarouselContentType.VIDEOS:
      return {aiContentType: AiCarouselContentType.VIDEOS, aiId};
    case AiCarouselContentType.AUDIO:
      return {aiContentType: AiCarouselContentType.AUDIO, aiId};
    default:
      return undefined;
  }
};

const AUDIO_GRID_ITEM_TYPES: ReadonlySet<GRID_ITEM_TYPE> = new Set([GRID_ITEM_TYPE.STOCK_AUDIO, GRID_ITEM_TYPE.USER_AUDIO, GRID_ITEM_TYPE.AUDIO]);

/**
 * True for any stock-panel item that adds audio: a stock/user audio tile, or an AI-voice carousel item (a CUSTOM item
 * tagged with the AUDIO ai-content type). Used to decide whether a drag-drop onto the canvas should open the timeline.
 */
export const isAudioStockItem = (item: GridItemStorage): boolean => {
  return AUDIO_GRID_ITEM_TYPES.has(item.type) || getAiCarouselItemMeta(item)?.aiContentType === AiCarouselContentType.AUDIO;
};

/**
 * On desktop, adding an audio item closes the stock popup so the timeline (opened by the
 * audio add itself in AudioClips.addAudioItemsData) is visible. True only on desktop for an
 * audio / AI-voice add — mobile keeps its own timeline-modal + selection-footer flow untouched.
 */
export const isDesktopAudioAdd = (items: GridItemStorage[]): boolean => {
  return !isMobile && items.some(isAudioStockItem);
};

const AI_VIDEO_THUMB_IMAGE_EXTENSION = 'jpg';
const AI_VIDEO_THUMB_VIDEO_EXTENSION = 'mp4';

export const getCarouselDataForAiVideoItem = (video: AiVideoBackendData): CarouselItemData => {
  const gridItemData: GridStockVideoItemStorage = {
    type: GRID_ITEM_TYPE.STOCK_VIDEO,
    id: `ai-video-${video.id}`,
    source: SOURCE.AI_TEXT_TO_IMAGE,
    imgThumbSrc: getAiVideoThumbUrl(video.hashedFilename, AI_VIDEO_THUMB_IMAGE_EXTENSION),
    videoThumbSrc: getAiVideoThumbUrl(video.hashedFilename, AI_VIDEO_THUMB_VIDEO_EXTENSION),
    isUHD: false,
    duration: `${video.videoProps.duration}`,
    data: {id: video.id},
    aiContentType: AiCarouselContentType.VIDEOS,
    aiId: video.id,
    width: video.videoProps.width,
    height: video.videoProps.height,
  };
  return {
    gridItemData,
    width: video.videoProps.width,
    height: video.videoProps.height,
  };
};

/**
 * Uploads an item picked from an AI carousel and returns the element to add, routing by its AI content type: voice
 * adds itself to the design (and shows its own loading) so it returns null; video uploads the generated video;
 * everything else (AI images) goes through the standard stock upload.
 */
export const uploadAiCarouselItem = async (item: GridItemStorage, shouldLoadDataUrlImage = false, dropPosition?: StockDropPosition): Promise<ItemData | null> => {
  const meta = getAiCarouselItemMeta(item);
  if (meta?.aiContentType === AiCarouselContentType.AUDIO) {
    await addGeneratedVoiceToDesign(meta.aiId);
    return null;
  }
  const element = meta?.aiContentType === AiCarouselContentType.VIDEOS ? await uploadGeneratedAiVideo(meta.aiId) : await uploadSelectedItem(item, shouldLoadDataUrlImage);
  return dropPosition ? {...element, left: dropPosition.left, top: dropPosition.top} : element;
};

export const getGridItemForAIGeneratedImageV2 = (aiGeneratedImageBackendData: AITextToImageBackendData): GridAiImageItemStorage => {
  const thumbSizeURL = getUrlForGeneratedAiImage(aiGeneratedImageBackendData.hashedFilename, aiGeneratedImageBackendData.imageProps.ext, MediaItemSizeType.THUMB);

  return {
    type: GRID_ITEM_TYPE.STOCK_IMAGE,
    id: aiGeneratedImageBackendData.id,
    thumbSrc: thumbSizeURL,
    highResSrc: getUrlForGeneratedAiImage(aiGeneratedImageBackendData.hashedFilename, aiGeneratedImageBackendData.imageProps.ext, MediaItemSizeType.HIGH_RES),
    source: SOURCE.AI_TEXT_TO_IMAGE,
    width: Number(aiGeneratedImageBackendData.imageProps.width),
    height: Number(aiGeneratedImageBackendData.imageProps.height),
    data: {
      uploaderId: aiGeneratedImageBackendData.uploaderId,
      id: aiGeneratedImageBackendData.id,
    },
    bottomContent: getPromptTextContentForAIImage(aiGeneratedImageBackendData.prompt.text),
  };
};

export const createKeepFiltersHiddenForTab = (trueTabs: StockPanelTabs[] = []): Record<StockPanelTabs, boolean> => {
  return Object.values(StockPanelTabs).reduce<Record<StockPanelTabs, boolean>>(
    (acc, tab) => {
      acc[tab] = trueTabs.includes(tab);
      return acc;
    },

    {} as Record<StockPanelTabs, boolean>
  );
};

interface SlideshowStockPanelConfig {
  tabsToShow: StockPanelTabs[];
  aiCarouselContentTypes: AiCarouselContentType[];
  blacklistedSources: StockSources[];
  shouldLoadDataUrlImage: boolean;
  addInBulk: boolean;
  enableSelectionFooter: boolean;
}

// Single source of truth for the stock panel config shared by every slideshow entry point (new slideshow + add slide, popup +
// inline, desktop + mobile). Slideshow is a visual flow: photos and videos, with the aggregated AI carousel (images + videos)
// rather than the text-to-image carousel. Centralised so these entry points can't drift apart.
export const getSlideshowStockPanelConfig = (): SlideshowStockPanelConfig => ({
  // ALL leads so the panel opens on the carousel browse view; the rest is the carousel row order.
  tabsToShow: [StockPanelTabs.ALL, StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS],
  aiCarouselContentTypes: [AiCarouselContentType.IMAGES, AiCarouselContentType.VIDEOS],
  blacklistedSources: getAnimatedStickerSources(),
  shouldLoadDataUrlImage: true,
  addInBulk: true,
  enableSelectionFooter: true,
});

/**
 * Pre-fills the AI generator prompt boxes with the user's active stock search term when a Create-with-AI
 * entry point opens an AI panel — the term is just placed in the box, nothing generates. AI Voice is
 * deliberately excluded (its box holds the literal script to speak, not a description of what to make).
 */
export const preFillAiPromptBoxesForAiPanelKey = (key: AiPanelKey, searchTerm: string | undefined): void => {
  if (searchTerm === undefined || searchTerm.trim() === '') {
    return;
  }
  if (key === 'ai-images' || key === 'ai-stickers' || key === 'ai-all') {
    preFillAiPromptBox(AI_IMAGE_PROMPT_BOX_ID, searchTerm);
  }
  if (key === 'ai-videos' || key === 'ai-all') {
    preFillAiPromptBox(AI_VIDEO_PROMPT_BOX_ID, searchTerm);
  }
};
